import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Building2, Users, TrendingUp, Database, HardDrive,
  Activity, DollarSign, Folder, FileText, AlertCircle, CheckCircle,
  XCircle, RefreshCw
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMCDetails = () => {
  const { amcId } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [amcId]);

  const fetchAnalytics = async () => {
    try {
      setError('');
      const response = await axios.get(`${API_URL}/owner/amcs/${amcId}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setError('Failed to load AMC analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatStorage = (mb) => {
    if (mb < 1024) {
      return `${mb.toFixed(2)} MB`;
    }
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <p className="mt-4 text-gray-900 font-medium">Failed to load AMC details</p>
          <button
            onClick={() => navigate('/owner/amcs')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            ← Back to AMC Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/owner/amcs')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <div className="flex items-center space-x-3">
                  <Building2 className="text-blue-600" size={32} />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{analytics.amc_name}</h1>
                    <div className="flex items-center mt-1">
                      {analytics.status ? (
                        <span className="flex items-center text-sm text-green-600">
                          <CheckCircle size={16} className="mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center text-sm text-red-600">
                          <XCircle size={16} className="mr-1" />
                          Inactive
                        </span>
                      )}
                      <span className="mx-2 text-gray-300">•</span>
                      <span className="text-sm text-gray-500">
                        Created: {new Date(analytics.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/owner/amcs/${amcId}/users`)}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="manage-amc-users-btn"
              >
                <Users size={16} className="mr-2" />
                Manage Users
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
              >
                <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Users */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="text-blue-600" size={24} />
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-1">Total Users</p>
            <p className="text-3xl font-bold text-gray-900">{analytics.users.total}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(analytics.users.by_role).map(([role, count]) => (
                <span key={role} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {role}: {count}
                </span>
              ))}
            </div>
          </div>

          {/* Funds */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Folder className="text-green-600" size={24} />
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-1">Total Funds</p>
            <p className="text-3xl font-bold text-gray-900">{analytics.funds.total}</p>
            {analytics.funds.recent_7_days > 0 && (
              <p className="mt-2 text-xs text-green-600">
                +{analytics.funds.recent_7_days} in last 7 days
              </p>
            )}
          </div>

          {/* Investments */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="text-purple-600" size={24} />
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-1">Total Investments</p>
            <p className="text-3xl font-bold text-gray-900">{analytics.investments.total}</p>
            {analytics.investments.recent_7_days > 0 && (
              <p className="mt-2 text-xs text-purple-600">
                +{analytics.investments.recent_7_days} in last 7 days
              </p>
            )}
          </div>

          {/* Storage */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <HardDrive className="text-orange-600" size={24} />
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-1">AWS S3 Storage</p>
            <p className="text-3xl font-bold text-gray-900">{formatStorage(analytics.storage.total_mb)}</p>
            <p className="mt-2 text-xs text-gray-500">
              {analytics.storage.file_count} files
            </p>
          </div>
        </div>

        {/* Financial Overview */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-8">
          <div className="flex items-center mb-6">
            <DollarSign className="text-gray-600 mr-2" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Financial Overview</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-2">Total Invested Amount</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(analytics.investments.total_invested_amount)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Current Portfolio Value</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(analytics.investments.total_current_value)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Total Gain/Loss</p>
              <p className={`text-2xl font-bold ${
                analytics.investments.total_current_value >= analytics.investments.total_invested_amount
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}>
                {formatCurrency(analytics.investments.total_current_value - analytics.investments.total_invested_amount)}
              </p>
              <p className={`text-sm ${
                analytics.investments.total_current_value >= analytics.investments.total_invested_amount
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}>
                {analytics.investments.total_invested_amount > 0
                  ? `${(((analytics.investments.total_current_value - analytics.investments.total_invested_amount) / analytics.investments.total_invested_amount) * 100).toFixed(2)}%`
                  : '0%'}
              </p>
            </div>
          </div>
        </div>

        {/* Storage Details */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center mb-6">
            <Database className="text-gray-600 mr-2" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Storage Details</h2>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center pb-4 border-b">
              <span className="text-gray-600">S3 Bucket Name</span>
              <span className="font-mono text-sm text-gray-900">{analytics.storage.bucket_name}</span>
            </div>
            <div className="flex justify-between items-center pb-4 border-b">
              <span className="text-gray-600">Total Files</span>
              <span className="font-semibold text-gray-900">{analytics.storage.file_count}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Storage Used</span>
              <span className="font-semibold text-gray-900">{formatStorage(analytics.storage.total_mb)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AMCDetails;
